import { useState, useMemo, useCallback, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuthStore } from '../../stores/authStore';
import { useBranchStore } from '../../stores/branchStore';
import { Button } from '../ui/Button';
import { BranchSelector } from '../ui/BranchSelector';
import { ImpersonationBanner } from '../ui/ImpersonationBanner';
import { OfflineBanner } from './OfflineBanner';
import { InstallPrompt } from './InstallPrompt';
import { MobileMenu } from './MobileMenu';
import {
  mobileBottomTabs,
  desktopNavItems,
  filterNavItems,
  groupBySection,
  type NavItem,
  type NavSection,
} from '../../config/navigation';
import { Badge } from '../ui/Badge';
import { useDashboardSummary } from '../../hooks/useDashboardAlerts';
import type { UserRole, Permission } from '@twg/shared';

interface AppShellProps {
  children: React.ReactNode;
}

export function AppShell({ children }: AppShellProps) {
  const { getPrimaryRole } = useAuthStore();
  const role = getPrimaryRole();

  // Techs get mobile-first layout, Admin/Ops get desktop layout
  const isMobileUser = role === 'Tech' || role === 'Holiday';

  if (isMobileUser) {
    return <MobileShell>{children}</MobileShell>;
  }

  return <DesktopShell>{children}</DesktopShell>;
}

// Mobile shell for technicians - bottom nav, minimal header
function MobileShell({ children }: { children: React.ReactNode }) {
  const { user, getIdToken } = useAuthStore();
  const { fetchUserBranches, isInitialized: branchInitialized } = useBranchStore();
  const location = useLocation();
  const navigate = useNavigate();
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  // Initialize branch store when auth is ready
  useEffect(() => {
    async function initBranches() {
      if (user && !branchInitialized) {
        const token = await getIdToken();
        if (token) {
          await fetchUserBranches(token);
        }
      }
    }
    initBranches();
  }, [user, branchInitialized, getIdToken, fetchUserBranches]);

  // Handle navigation - opens menu for special items, navigates for regular items
  const handleNavClick = (item: NavItem) => {
    if (item.isSpecial) {
      setIsMenuOpen(true);
    } else {
      navigate(item.path);
    }
  };

  return (
    <div className="min-h-screen bg-sage-50 flex flex-col">
      {/* Impersonation banner (shows when viewing via simulator) */}
      <ImpersonationBanner />

      {/* Offline indicator and sync status */}
      <OfflineBanner />

      {/* PWA install prompt */}
      <InstallPrompt />

      {/* Skip to main content link for accessibility */}
      <a
        href="#main-content"
        className="sr-only focus:not-sr-only focus:absolute focus:top-4 focus:left-4 focus:z-[100] focus:bg-forest-700 focus:text-white focus:px-4 focus:py-2 focus:rounded-lg focus:shadow-lg"
      >
        Skip to main content
      </a>

      {/* Main content - grows to fill space */}
      <main id="main-content" tabIndex={-1} className="flex-1 flex flex-col outline-none">
        {children}
      </main>

      {/* Bottom Navigation - fixed */}
      <nav data-bottom-nav className="fixed bottom-0 left-0 right-0 bg-white border-t border-sage-200 safe-bottom z-50">
        <div className="flex justify-around py-2">
          {mobileBottomTabs.map((item) => {
            // Menu item should not show active styling
            const isActive = !item.isSpecial && (
              location.pathname === item.path ||
              (item.path === '/' && location.pathname === '/schedule')
            );
            return (
              <button
                key={item.path}
                onClick={() => handleNavClick(item)}
                className={`flex flex-col items-center px-4 py-2 rounded-xl transition-colors touch-manipulation min-w-touch min-h-touch ${
                  isActive
                    ? 'text-forest-700 bg-sage-100'
                    : 'text-sage-500 hover:text-forest-600'
                }`}
              >
                <span className="text-xl mb-0.5">{item.icon}</span>
                <span className="text-xs font-medium">{item.label}</span>
              </button>
            );
          })}
        </div>
      </nav>

      {/* Spacer for bottom nav */}
      <div className="h-20" />

      {/* Mobile Menu (hamburger) */}
      <MobileMenu isOpen={isMenuOpen} onClose={() => setIsMenuOpen(false)} />
    </div>
  );
}

// Section header labels for the sidebar
const sectionLabels: Record<NavSection, string> = {
  main: 'NAVIGATION',
  tools: 'TOOLS',
  alerts: 'ALERTS',
  admin: 'ADMIN',
  management: 'MANAGEMENT',
};

// Sidebar button subcomponent
function SidebarButton({
  item,
  isActive,
  onClick,
  collapsed = false,
  badgeCount,
}: {
  item: NavItem;
  isActive: boolean;
  onClick: () => void;
  collapsed?: boolean;
  badgeCount?: number;
}) {
  return (
    <button
      onClick={onClick}
      title={collapsed ? item.label : undefined}
      className={`w-full flex items-center gap-3 ${collapsed ? 'px-3 justify-center' : 'px-6'} py-3 text-left transition-colors ${
        isActive
          ? 'bg-forest-800 text-white border-r-2 border-primary-500'
          : 'text-forest-300 hover:bg-forest-800 hover:text-white'
      }`}
    >
      <span className="text-lg">{item.icon}</span>
      {!collapsed && <span className="font-medium flex-1">{item.label}</span>}
      {!collapsed && badgeCount !== undefined && badgeCount > 0 && (
        <Badge variant="info" size="sm">{badgeCount}</Badge>
      )}
    </button>
  );
}

// Desktop shell for managers/ops - sidebar nav
function DesktopShell({ children }: { children: React.ReactNode }) {
  const { user, logout, isSuperAdmin, getIdToken } = useAuthStore();
  const { fetchUserBranches, isInitialized: branchInitialized } = useBranchStore();
  const location = useLocation();
  const navigate = useNavigate();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(() => {
    // Load from localStorage
    const saved = localStorage.getItem('sidebar-collapsed');
    return saved === 'true';
  });

  // Fetch dashboard summary for badge counts
  const { data: summary } = useDashboardSummary();

  // Get badge count for email nav item
  const getBadgeCount = (item: NavItem): number | undefined => {
    if (item.badge === 'emailUnread' && summary?.email?.unread) {
      return summary.email.unread;
    }
    return undefined;
  };

  // Initialize branch store when auth is ready
  useEffect(() => {
    async function initBranches() {
      if (user && !branchInitialized) {
        const token = await getIdToken();
        if (token) {
          await fetchUserBranches(token);
        }
      }
    }
    initBranches();
  }, [user, branchInitialized, getIdToken, fetchUserBranches]);

  // Save sidebar state to localStorage
  const toggleSidebar = () => {
    setIsSidebarCollapsed((prev) => {
      localStorage.setItem('sidebar-collapsed', String(!prev));
      return !prev;
    });
  };

  // Extract roles and permissions from user
  const userRoles = (user?.roles || []) as UserRole[];
  const userPermissions = (user?.permissions || []) as Permission[];

  // Filter and group navigation items
  const filteredItems = filterNavItems(desktopNavItems, userRoles, userPermissions, isSuperAdmin);
  const sections = groupBySection(filteredItems);

  const greeting = useMemo(() => {
    const hour = new Date().getHours();
    if (hour < 12) return 'Good morning';
    if (hour < 17) return 'Good afternoon';
    return 'Good evening';
  }, []);

  // Check if a nav item is active
  const isNavItemActive = useCallback((item: NavItem) => {
    return location.pathname === item.path ||
      (item.path === '/' && location.pathname === '/schedule');
  }, [location.pathname]);

  // Render a section of navigation items
  const renderSection = (sectionKey: NavSection) => {
    const items = sections[sectionKey];
    if (!items || items.length === 0) return null;

    return (
      <div key={sectionKey} className="mb-4">
        {!isSidebarCollapsed && (
          <h3 className="px-6 py-2 text-xs font-semibold uppercase tracking-wider text-forest-500">
            {sectionLabels[sectionKey]}
          </h3>
        )}
        {items.map((item) => (
          <SidebarButton
            key={item.path}
            item={item}
            isActive={isNavItemActive(item)}
            onClick={() => navigate(item.path)}
            collapsed={isSidebarCollapsed}
            badgeCount={getBadgeCount(item)}
          />
        ))}
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-sage-50 flex flex-col">
      {/* Impersonation banner (shows when viewing via simulator) */}
      <ImpersonationBanner />

      {/* Offline indicator and sync status */}
      <OfflineBanner />

      {/* PWA install prompt */}
      <InstallPrompt />

      <div className="flex-1 flex">
        {/* Skip to main content link for accessibility */}
        <a
          href="#main-content"
          className="sr-only focus:not-sr-only focus:absolute focus:top-4 focus:left-4 focus:z-[100] focus:bg-forest-700 focus:text-white focus:px-4 focus:py-2 focus:rounded-lg focus:shadow-lg"
        >
          Skip to main content
        </a>

        {/* Sidebar */}
        <aside className={`hidden lg:flex flex-col flex-shrink-0 bg-forest-900 text-white transition-all duration-300 ${
        isSidebarCollapsed ? 'w-16' : 'w-64'
      }`}>
        {/* Logo + Collapse toggle */}
        <div className={`border-b border-forest-800 ${isSidebarCollapsed ? 'p-3' : 'p-6'}`}>
          <div className="flex items-center gap-3">
            <div className={`rounded-xl overflow-hidden flex-shrink-0 ${
              isSidebarCollapsed ? 'w-10 h-10' : 'w-10 h-10'
            }`}>
              <img src="/logo.png" alt="TWG Logo" className="w-full h-full object-contain bg-white p-1 rounded-xl" />
            </div>
            {!isSidebarCollapsed && (
              <div className="flex-1 min-w-0">
                <h1 className="font-display text-xl font-semibold">TWG</h1>
                <p className="text-forest-400 text-xs">Field Operations</p>
              </div>
            )}
          </div>
        </div>

        {/* Collapse toggle button */}
        <button
          onClick={toggleSidebar}
          className={`my-2 p-2 rounded-lg hover:bg-forest-800 text-forest-400 hover:text-white transition-colors ${
            isSidebarCollapsed ? 'mx-auto' : 'ml-auto mr-3'
          }`}
          title={isSidebarCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
        >
          <svg
            className={`w-5 h-5 transition-transform duration-300 ${isSidebarCollapsed ? 'rotate-180' : ''}`}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 19l-7-7 7-7m8 14l-7-7 7-7" />
          </svg>
        </button>

        {/* Nav Items - sectioned */}
        <nav className="flex-1 py-2 overflow-y-auto">
          {renderSection('main')}
          {renderSection('tools')}
          {renderSection('management')}
          {renderSection('admin')}
        </nav>

        {/* User info */}
        <div className={`border-t border-forest-800 ${isSidebarCollapsed ? 'p-2' : 'p-4'}`}>
          {isSidebarCollapsed ? (
            // Collapsed: just show avatar
            <div
              className="w-10 h-10 mx-auto rounded-full bg-forest-700 flex items-center justify-center text-sm font-medium cursor-pointer hover:bg-forest-600 transition-colors"
              title={`${user?.name}\n${user?.email}`}
              onClick={logout}
            >
              {user?.name?.charAt(0) || 'U'}
            </div>
          ) : (
            // Expanded: show full user info
            <>
              <div className="flex items-center gap-3 mb-3">
                <div className="w-10 h-10 rounded-full bg-forest-700 flex items-center justify-center text-sm font-medium">
                  {user?.name?.charAt(0) || 'U'}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium truncate">{user?.name}</p>
                  <p className="text-xs text-forest-400 truncate">{user?.email}</p>
                </div>
              </div>
              <Button
                variant="ghost"
                size="sm"
                onClick={logout}
                className="w-full text-forest-300 hover:text-white hover:bg-forest-800"
              >
                Sign Out
              </Button>
            </>
          )}
        </div>
      </aside>

      {/* Main content area */}
      <div className="flex-1 flex flex-col min-h-screen overflow-hidden">
        {/* Top header for mobile/tablet */}
        <header className="lg:hidden bg-forest-900 text-white px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-xl bg-primary-500 flex items-center justify-center">
              <span className="text-lg">🌿</span>
            </div>
            <span className="font-display font-semibold">TWG</span>
          </div>
          <div className="flex items-center gap-2">
            {/* Branch selector - only visible for multi-branch users */}
            <BranchSelector variant="header" />
            {/* Hamburger menu button */}
            <button
              onClick={() => setIsMenuOpen(true)}
              className="w-10 h-10 flex items-center justify-center rounded-lg hover:bg-forest-800 transition-colors touch-manipulation"
              aria-label="Open menu"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
              </svg>
            </button>
          </div>
        </header>

        {/* Mobile menu for tablet/mobile */}
        <MobileMenu isOpen={isMenuOpen} onClose={() => setIsMenuOpen(false)} />

        {/* Desktop header */}
        <header className="hidden lg:block bg-white border-b border-sage-200 px-8 py-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-sage-600">{greeting}</p>
              <h2 className="text-xl font-display font-semibold text-forest-900">
                {user?.name}
              </h2>
            </div>
            <div className="flex items-center gap-4">
              {/* Branch selector - only visible for multi-branch users */}
              <BranchSelector variant="inline" />
              <Button variant="primary" size="sm" onClick={() => navigate('/workorders')}>
                + New Work Order
              </Button>
            </div>
          </div>
        </header>

        {/* Page content */}
        <main id="main-content" tabIndex={-1} className="flex-1 overflow-auto outline-none">
          {children}
        </main>
      </div>
      </div>
    </div>
  );
}
